/**
 * Calculateur de paquets de bardeaux — page d'acquisition organique (US).
 *
 * Requête visée (Planificateur Google, US, relevé 2026-09-15) :
 * « how many roofing shingles in a bundle » 3 600 recherches par mois, plus
 * la famille « how many sq ft in a bundle of shingles » (1 900), le tout en
 * concurrence faible.
 *
 * La page répond d'abord à la question posée (3 paquets par square, environ
 * 33 pi² par paquet), puis calcule la commande complète : paquets, bande de
 * départ, faîtière, clous. C'est ce calcul-là qui la rend utile plutôt que
 * simplement lue.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import CoquilleOutil from "@/components/CoquilleOutil";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { multiplicateur, arrondi, millier, PI2_PAR_SQUARE } from "@/lib/outils";

const URL = "https://roofborder.com/shingle-bundle-calculator";

// Profils courants. Le nombre de paquets par square est ce qui change d'un
// bardeau à l'autre ; tout le reste du calcul en découle.
const PROFILS = [
  { id: "arch", nom: "Architectural (laminated)", paquets: 3 },
  { id: "3tab", nom: "Three-tab", paquets: 3 },
  { id: "designer", nom: "Designer / heavyweight", paquets: 4 },
  { id: "premium", nom: "Premium slate-look", paquets: 5 },
];

const FAQ = [
  {
    q: "How many shingles are in a bundle?",
    r: "A bundle of architectural shingles holds about 21 to 26 physical shingles, and a bundle of three-tab about 26. The number that matters for ordering is coverage, not count: three bundles cover one roofing square, which is 100 square feet.",
  },
  {
    q: "How many square feet does a bundle of shingles cover?",
    r: "About 33.3 square feet for a standard three-bundle-per-square shingle. Heavier designer profiles run four or five bundles per square, so each bundle covers 25 or 20 square feet.",
  },
  {
    q: "How many bundles do I need for a 2,000 square foot roof?",
    r: "Twenty squares is 60 bundles before waste, 66 bundles with ten percent added. Careful though: 2,000 square feet of footprint on a 6/12 roof is 2,236 square feet of actual roof, which is 74 bundles with waste.",
  },
  {
    q: "How much does a bundle of shingles weigh?",
    r: "Between 60 and 80 pounds for standard architectural shingles, and up to 100 pounds for heavyweight profiles. Three bundles per square means a 25-square roof lands roughly 5,000 pounds of shingles on the deck.",
  },
  {
    q: "Do I need starter strip and ridge cap on top of the bundles?",
    r: "Yes. Starter runs the eaves and rakes, and ridge cap covers the ridges and hips. Neither comes out of your field bundles, and forgetting them is the most common reason a crew runs short on day two.",
  },
  {
    q: "How many bundles are on a pallet?",
    r: "Most suppliers palletize 42 bundles, which is 14 squares of a standard three-bundle shingle. It varies by manufacturer, so confirm before you plan the lift.",
  },
];

export default function CalculateurBardeaux() {
  const [empreinte, setEmpreinte] = useState(2000);
  const [rise, setRise] = useState(6);
  const [perte, setPerte] = useState(10);
  const [profil, setProfil] = useState("arch");
  const [perimetre, setPerimetre] = useState(180);
  const [faite, setFaite] = useState(60);

  const p = PROFILS.find((x) => x.id === profil) || PROFILS[0];
  const mult = multiplicateur(Number(rise) || 0, 12);
  const surface = (Number(empreinte) || 0) * mult;
  const carres = (surface / PI2_PAR_SQUARE) * (1 + (Number(perte) || 0) / 100);
  const paquets = Math.ceil(carres * p.paquets);

  // Bande de départ : ~100 pi linéaires par rouleau. Faîtière : ~20 pi lin. par
  // paquet de capuchons. Clous : environ 320 par square à 4 clous par bardeau.
  const departRouleaux = Math.ceil((Number(perimetre) || 0) / 100);
  const faitePaquets = Math.ceil((Number(faite) || 0) / 20);
  const clous = Math.ceil((carres * 320) / 100) * 100;

  const donnees = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.r },
          })),
        },
        {
          "@type": "WebApplication",
          name: "Shingle Bundle Calculator",
          url: URL,
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          publisher: { "@type": "Organization", name: "Roof Border", url: "https://roofborder.com" },
        },
      ],
    }),
    []
  );

  useSeo({
    titre: "Shingle Bundle Calculator — How Many Bundles Per Square | Roof Border",
    description:
      "Free shingle bundle calculator: three bundles cover one roofing square (100 sq ft). Enter footprint, pitch and waste to get bundles, starter strip, ridge cap and nails for the order.",
    canonique: URL,
    donnees,
  });

  const champ =
    "w-full rounded-md border border-line-strong bg-field px-3 py-2 text-base text-ink focus:outline-none focus:ring-2 focus:ring-brand/40";

  return (
    <CoquilleOutil actuel="/shingle-bundle-calculator">
      <p className="text-xs font-bold tracking-widest uppercase text-brand-ink">Free tool</p>
      <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">Shingle Bundle Calculator</h1>
      <p className="mt-4 text-lg text-ink-soft max-w-2xl">
        Three bundles cover one roofing square, which is 100 square feet of roof — so one bundle
        covers about 33 square feet. Enter the roof below and this works out the whole order:
        bundles, starter strip, ridge cap and nails.
      </p>

      <section className="mt-10 grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-line p-6 bg-surface">
          <h2 className="text-lg font-bold">Your roof</h2>

          <label className="block mt-5 text-sm font-medium" htmlFor="empreinte">
            Footprint (sq ft)
          </label>
          <input
            id="empreinte"
            type="number"
            min="0"
            step="10"
            value={empreinte}
            onChange={(e) => setEmpreinte(e.target.value)}
            className={`mt-1 ${champ}`}
            data-testid="bardeaux-empreinte"
          />

          <label className="block mt-4 text-sm font-medium" htmlFor="rise">
            Pitch (rise over 12)
          </label>
          <input
            id="rise"
            type="number"
            min="0"
            max="18"
            step="0.5"
            value={rise}
            onChange={(e) => setRise(e.target.value)}
            className={`mt-1 ${champ}`}
            data-testid="bardeaux-rise"
          />

          <label className="block mt-4 text-sm font-medium" htmlFor="profil">
            Shingle type
          </label>
          <select
            id="profil"
            value={profil}
            onChange={(e) => setProfil(e.target.value)}
            className={`mt-1 ${champ}`}
            data-testid="bardeaux-profil"
          >
            {PROFILS.map((x) => (
              <option key={x.id} value={x.id}>
                {x.nom} — {x.paquets} bundles/square
              </option>
            ))}
          </select>

          <label className="block mt-4 text-sm font-medium" htmlFor="perte">
            Waste factor (%)
          </label>
          <input
            id="perte"
            type="number"
            min="0"
            max="40"
            step="1"
            value={perte}
            onChange={(e) => setPerte(e.target.value)}
            className={`mt-1 ${champ}`}
            data-testid="bardeaux-perte"
          />

          <label className="block mt-4 text-sm font-medium" htmlFor="perimetre">
            Eaves + rakes (linear ft)
          </label>
          <input
            id="perimetre"
            type="number"
            min="0"
            step="5"
            value={perimetre}
            onChange={(e) => setPerimetre(e.target.value)}
            className={`mt-1 ${champ}`}
            data-testid="bardeaux-perimetre"
          />

          <label className="block mt-4 text-sm font-medium" htmlFor="faite">
            Ridges + hips (linear ft)
          </label>
          <input
            id="faite"
            type="number"
            min="0"
            step="5"
            value={faite}
            onChange={(e) => setFaite(e.target.value)}
            className={`mt-1 ${champ}`}
            data-testid="bardeaux-faite"
          />
        </div>

        <div className="rounded-xl border border-line p-6 bg-surface" data-testid="bardeaux-resultat">
          <h2 className="text-lg font-bold">Your order</h2>
          <dl className="mt-5 space-y-3 text-base">
            <div className="flex justify-between border-b border-line pb-2">
              <dt className="text-ink-soft">Sloped roof area</dt>
              <dd className="font-bold">{millier(surface)} sq ft</dd>
            </div>
            <div className="flex justify-between border-b border-line pb-2">
              <dt className="text-ink-soft">Squares incl. {arrondi(Number(perte) || 0, 0)} % waste</dt>
              <dd className="font-bold">{arrondi(carres, 1)} sq</dd>
            </div>
            <div className="flex justify-between border-b border-line pb-2">
              <dt className="text-ink-soft">Field bundles ({p.paquets}/square)</dt>
              <dd className="font-bold text-xl">{paquets}</dd>
            </div>
            <div className="flex justify-between border-b border-line pb-2">
              <dt className="text-ink-soft">Starter strip rolls (100 lin ft)</dt>
              <dd className="font-bold">{departRouleaux}</dd>
            </div>
            <div className="flex justify-between border-b border-line pb-2">
              <dt className="text-ink-soft">Ridge cap bundles (20 lin ft)</dt>
              <dd className="font-bold">{faitePaquets}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ink-soft">Roofing nails (4 per shingle)</dt>
              <dd className="font-bold">{millier(clous)}</dd>
            </div>
          </dl>

          <div className="mt-6 rounded-lg bg-hover-strong p-4">
            <p className="text-sm text-ink-soft">
              Starter and ridge cap do not come out of the field bundles. Running short on day two
              is almost always one of those two.
            </p>
            <Link to="/measure" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-brand-ink hover:underline">
              Get eaves, ridges and area measured for you <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="mt-16 max-w-3xl">
        <h2 className="text-2xl font-extrabold tracking-tight">Bundles per square, by shingle</h2>
        <div className="mt-5 overflow-x-auto rounded-xl border border-line">
          <table className="w-full text-sm">
            <thead className="bg-hover-strong text-left">
              <tr>
                <th className="px-4 py-3 font-bold">Shingle</th>
                <th className="px-4 py-3 font-bold">Bundles per square</th>
                <th className="px-4 py-3 font-bold">Coverage per bundle</th>
                <th className="px-4 py-3 font-bold">Bundles for 25 sq</th>
              </tr>
            </thead>
            <tbody>
              {PROFILS.map((x) => (
                <tr key={x.id} className="border-t border-line">
                  <td className="px-4 py-2.5 font-medium">{x.nom}</td>
                  <td className="px-4 py-2.5">{x.paquets}</td>
                  <td className="px-4 py-2.5">{arrondi(PI2_PAR_SQUARE / x.paquets, 1)} sq ft</td>
                  <td className="px-4 py-2.5">{25 * x.paquets}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-5 text-ink-soft">
          The physical shingle count in a bundle — 21, 26, whatever the wrapper says — is not what
          you order by. Coverage is. Two bundles with different counts can cover the same 33 square
          feet, and it is the coverage that has to match the roof.
        </p>
      </section>

      <section className="mt-16 max-w-3xl">
        <h2 className="text-2xl font-extrabold tracking-tight">What crews forget to order</h2>
        <ul className="mt-4 space-y-3 text-ink-soft list-disc pl-5">
          <li>
            <span className="text-ink font-medium">Starter strip</span> along every eave and rake.
            Cutting field shingles for starter voids most wind warranties.
          </li>
          <li>
            <span className="text-ink font-medium">Ridge cap</span> for ridges and hips. A hip roof
            eats far more cap than a gable of the same size.
          </li>
          <li>
            <span className="text-ink font-medium">Valley material</span>, ice and water shield at
            the eaves, and pipe boots. None of it comes out of the bundle count.
          </li>
          <li>
            <span className="text-ink font-medium">Extra waste on cut-up roofs.</span> Ten percent
            is for a simple gable. Fifteen is honest on a hip roof with dormers and valleys.
          </li>
        </ul>
      </section>

      <section className="mt-16 max-w-3xl">
        <h2 className="text-2xl font-extrabold tracking-tight">Questions</h2>
        <div className="mt-5 space-y-5">
          {FAQ.map((f) => (
            <div key={f.q} className="border-t border-line pt-5">
              <h3 className="font-bold">{f.q}</h3>
              <p className="mt-2 text-ink-soft">{f.r}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-16 rounded-xl border border-line bg-surface p-8 text-center">
        <h2 className="text-2xl font-extrabold tracking-tight">Order from measurements, not guesses</h2>
        <p className="mt-3 text-ink-soft max-w-xl mx-auto">
          Put in an address. Roof Border measures every face, pitch and ridge from satellite
          imagery, then prices the materials and the labor into a branded quote.
        </p>
        <Link to="/measure">
          <Button className="mt-6 rounded-md h-12 px-7 text-base">
            Measure a roof <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </section>
    </CoquilleOutil>
  );
}
